import React from "react";
import { Twitter, Linkedin, Instagram, Film, Hash } from "lucide-react";
import Markdown from "./Markdown";
import { useLang } from "../context/LangContext";
import { BACKEND } from "../lib/api";

const PLAT_ICON = { twitter: Twitter, linkedin: Linkedin, instagram: Instagram };

export function SocialPostCard({ post }) {
  const Icon = PLAT_ICON[post.platform] || Hash;
  return (
    <div className="rounded-xl border border-white/10 bg-card p-4" data-testid={`social-post-${post.platform}`}>
      <div className="flex items-center gap-2 mb-2 text-sm font-semibold capitalize">
        <Icon className="h-4 w-4 text-primary" /> {post.platform}
      </div>
      <p className="text-sm text-slate-300 whitespace-pre-wrap">{post.content}</p>
      {post.hashtags?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {post.hashtags.map((h, i) => (
            <span key={i} className="text-xs text-primary">#{String(h).replace(/^#/, "")}</span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PreviewContent({ type, content }) {
  const { t } = useLang();
  const c = content || {};

  if (type === "article") {
    return (
      <div className="space-y-5">
        {c.images?.hero && (
          <img src={c.images.hero} alt="" className="w-full max-h-72 object-cover rounded-xl border border-white/10" />
        )}
        {c.excerpt && <p className="text-slate-400 italic">{c.excerpt}</p>}
        <Markdown>{c.body_markdown}</Markdown>
        {c.seo && (c.seo.meta_title || c.seo.meta_description) && (
          <div className="rounded-xl border border-white/10 bg-card p-4">
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">SEO</div>
            <p className="text-sm"><b>{c.seo.meta_title}</b></p>
            <p className="text-sm text-slate-400">{c.seo.meta_description}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(c.seo.keywords || []).map((k, i) => (
                <span key={i} className="text-xs rounded-full bg-white/5 border border-white/10 px-2 py-0.5">{k}</span>
              ))}
            </div>
          </div>
        )}
        {c.video_script && (
          <div className="rounded-xl border border-white/10 bg-card p-4">
            <div className="flex items-center gap-2 text-sm font-semibold mb-2"><Film className="h-4 w-4 text-primary" />{t("Guião de vídeo", "Video script")}</div>
            <p className="text-sm text-slate-300 whitespace-pre-wrap">{c.video_script.script}</p>
          </div>
        )}
        {c.social_posts?.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-2">
            {c.social_posts.map((p, i) => <SocialPostCard key={i} post={p} />)}
          </div>
        )}
      </div>
    );
  }

  if (type === "video") {
    return (
      <div className="space-y-5">
        {c.video_url ? (
          <video data-testid="video-player" src={`${BACKEND}${c.video_url}`} poster={c.thumbnail || undefined} controls playsInline
            className="w-full max-h-[70vh] rounded-xl border border-white/10 bg-black" />
        ) : c.thumbnail ? (
          <img src={c.thumbnail} alt="" className="w-full max-h-72 object-cover rounded-xl border border-white/10" />
        ) : null}
        <div className="rounded-xl border border-white/10 bg-card p-4">
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">{t("Narração", "Narration")}</div>
          <p className="text-sm text-slate-300 whitespace-pre-wrap">{c.script}</p>
        </div>
        {c.scenes?.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("Cenas", "Scenes")}</div>
            {c.scenes.map((s, i) => (
              <div key={i} className="rounded-lg border border-white/10 bg-card p-3 text-sm">
                <div className="font-semibold text-primary">{s.caption || s.scene || `${t("Cena", "Scene")} ${i + 1}`}</div>
                <div className="text-slate-300 mt-1">{s.narration}</div>
                {s.visual && <div className="text-slate-500 text-xs mt-1"><Film className="inline h-3 w-3 mr-1" />{s.visual}</div>}
              </div>
            ))}
          </div>
        )}
        {c.hashtags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {c.hashtags.map((h, i) => <span key={i} className="text-xs text-primary">#{String(h).replace(/^#/, "")}</span>)}
          </div>
        )}
      </div>
    );
  }

  if (type === "social_post") {
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        {(c.posts || []).map((p, i) => <SocialPostCard key={i} post={p} />)}
      </div>
    );
  }
  return <pre className="text-xs text-slate-400 overflow-auto">{JSON.stringify(c, null, 2)}</pre>;
}
